import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { usePermissions } from '@/hooks/usePermissions';
import { Shield, Plus, Edit, Trash2, X, Save, Search, Home, ChevronRight } from 'lucide-react';
import { useToast } from '@/contexts/ToastContext';
import { roleService, Role } from '@/services/role.service';
import { confirmDelete, showDeleteSuccess, showDeleteError } from '@/utils/deleteConfirmation';

export function RoleManagement() {
  const { hasPermission } = usePermissions();
  const { showToast } = useToast();
  const queryClient = useQueryClient();

  // Permissions
  const canView = hasPermission('role_management_view');
  const canEdit = hasPermission('role_management_edit');
  const canDelete = hasPermission('role_management_delete');

  // State
  const [searchQuery, setSearchQuery] = useState('');
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formData, setFormData] = useState({ name: '', description: '' });

  // Fetch roles from API
  const { data: roles = [], isLoading } = useQuery({
    queryKey: ['roles'],
    queryFn: async () => {
      const token = localStorage.getItem('accessToken') || '';
      return await roleService.getAllRoles(token, { isActive: true });
    },
    enabled: canView,
  });

  // Create role mutation
  const createMutation = useMutation({
    mutationFn: async (data: { name: string; description?: string }) => {
      const token = localStorage.getItem('accessToken') || '';
      return await roleService.createRole(data, token);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['roles'] });
      setFormData({ name: '', description: '' });
      setIsAddingNew(false);
      showToast('success', 'Role Added', 'Role has been created successfully');
    },
    onError: (error: any) => {
      showToast('error', 'Error', error.response?.data?.message || 'Failed to create role');
    },
  });

  // Update role mutation
  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: { name?: string; description?: string } }) => {
      const token = localStorage.getItem('accessToken') || '';
      return await roleService.updateRole(id, data, token);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['roles'] });
      setEditingId(null);
      setFormData({ name: '', description: '' });
      showToast('success', 'Role Updated', 'Role has been updated successfully');
    },
    onError: (error: any) => {
      showToast('error', 'Error', error.response?.data?.message || 'Failed to update role');
    },
  });

  // Delete role mutation
  const deleteMutation = useMutation({
    mutationFn: async (roleId: string) => {
      const token = localStorage.getItem('accessToken') || '';
      return await roleService.deleteRole(roleId, token);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['roles'] });
      showDeleteSuccess('Role has been deleted successfully.');
    },
    onError: (error: any) => {
      showDeleteError(error.response?.data?.message || 'Failed to delete role');
    },
  });

  // Check view permission
  if (!canView) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <Shield className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-700">Access Denied</h2>
          <p className="text-gray-500 mt-2">You don't have permission to view roles</p>
        </div>
      </div>
    );
  }

  // Filter roles
  const filteredRoles = roles.filter(role =>
    role.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (role.description || '').toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <Shield className="w-16 h-16 text-gray-400 mx-auto mb-4 animate-pulse" />
          <p className="text-gray-500">Loading roles...</p>
        </div>
      </div>
    );
  }

  // Handlers
  const handleAdd = () => {
    if (!canEdit) {
      showToast('error', 'Permission Denied', 'You cannot add roles');
      return;
    }

    if (!formData.name.trim()) {
      showToast('error', 'Validation Error', 'Role name is required');
      return;
    }

    createMutation.mutate({
      name: formData.name,
      description: formData.description || undefined,
    });
  };

  const handleUpdate = () => {
    if (!canEdit || !editingId) {
      showToast('error', 'Permission Denied', 'You cannot edit roles');
      return;
    }

    updateMutation.mutate({
      id: editingId,
      data: {
        name: formData.name,
        description: formData.description || undefined,
      },
    });
  };

  const handleDeleteRole = async (id: string, name: string) => {
    if (!canDelete) {
      showToast('error', 'Permission Denied', 'You cannot delete roles');
      return;
    }

    const confirmed = await confirmDelete({
      title: 'Delete Role?',
      itemName: name,
    });

    if (confirmed) {
      deleteMutation.mutate(id);
    }
  };

  const startEdit = (role: Role) => {
    setEditingId(role._id);
    setFormData({ name: role.name, description: role.description || '' });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setFormData({ name: '', description: '' });
  };

  return (
    <div className="space-y-6">
      {/* Breadcrumbs */}
      <nav className="flex items-center space-x-2 text-sm text-gray-600">
        <Link to="/dashboard" className="flex items-center hover:text-blue-600 transition-colors">
          <Home className="w-4 h-4" />
        </Link>
        <ChevronRight className="w-4 h-4" />
        <span className="text-gray-400">Settings</span>
        <ChevronRight className="w-4 h-4" />
        <span className="text-gray-900 font-semibold">Role Management</span>
      </nav>

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
            <Shield className="w-8 h-8 text-primary-600" />
            Role Management
          </h1>
          <p className="text-sm text-gray-600 mt-1">
            Manage system roles and their descriptions
          </p>
        </div>
        {canEdit && (
          <button
            onClick={() => setIsAddingNew(true)}
            className="btn-primary flex items-center text-sm"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Role
          </button>
        )}
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
        <input
          type="text"
          placeholder="Search roles..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full pl-12 pr-4 py-3 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
        />
      </div>

      {/* Add New Role Form */}
      {isAddingNew && (
        <div className="bg-white rounded-xl border-2 border-primary-200 shadow-sm p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Add New Role</h2>
            <button
              onClick={() => {
                setIsAddingNew(false);
                setFormData({ name: '', description: '' });
              }}
              className="p-2 hover:bg-gray-100 rounded-lg"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Role Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Content Manager"
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Description
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Brief description of this role..."
                rows={3}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500"
              />
            </div>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setIsAddingNew(false);
                  setFormData({ name: '', description: '' });
                }}
                className="px-6 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-xl hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleAdd}
                className="px-6 py-2.5 text-sm font-medium text-white bg-primary-600 rounded-xl hover:bg-primary-700 flex items-center"
              >
                <Save className="w-4 h-4 mr-2" />
                Add Role
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Roles List */}
      <div className="grid gap-4">
        {filteredRoles.map((role) => (
          <div
            key={role._id}
            className="bg-white rounded-xl border border-gray-200 hover:shadow-md transition-all p-6"
          >
            {editingId === role._id ? (
              // Edit Mode
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Role Name
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Description
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows={2}
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg"
                  />
                </div>

                <div className="flex justify-end gap-3">
                  <button
                    onClick={cancelEdit}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleUpdate}
                    className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 flex items-center"
                  >
                    <Save className="w-4 h-4 mr-2" />
                    Save
                  </button>
                </div>
              </div>
            ) : (
              // View Mode
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <Shield className="w-5 h-5 text-primary-600" />
                    <h3 className="text-lg font-semibold text-gray-900">{role.name}</h3>
                  </div>
                  <p className="text-sm text-gray-600 mb-2">{role.description}</p>
                  <p className="text-xs text-gray-400">
                    Created: {new Date(role.createdAt).toLocaleDateString()}
                  </p>
                </div>

                <div className="flex items-center gap-2">
                  {canEdit && (
                    <button
                      onClick={() => startEdit(role)}
                      className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                      title="Edit role"
                    >
                      <Edit className="w-5 h-5" />
                    </button>
                  )}
                  {canDelete && (
                    <button
                      onClick={() => handleDeleteRole(role._id, role.name)}
                      className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      title="Delete role"
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        ))}

        {filteredRoles.length === 0 && (
          <div className="text-center py-12">
            <Shield className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">No roles found</p>
          </div>
        )}
      </div>

    </div>
  );
}
